import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import './OAuthCallback.css';

/**
 * OAuth callback landing page.
 *
 * Nexus cutover 2026-05-25 (Phase F): Nexus puts tokens in the URL
 * FRAGMENT (after `#`), not the query string — fragments never reach
 * origin servers via Referer headers + aren't logged by intermediaries.
 * Param names are snake_case (matches the legacy K-Stack gateway +
 * iOS OAuthService.swift). Error responses still come via query
 * string (per Nexus's redirectError helper).
 *
 * Success fragment shape (Nexus oauth/routes.ts ~line 258):
 *   #oauth_result=success&access_token=…&id_token=…&refresh_token=…
 *    &expires_in=<seconds>&provider=google
 *
 * Error query shape:
 *   ?oauth_result=error&oauth_error=<reason>
 */
const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Strip the leading '#' or '?' before URLSearchParams parses.
    const fragmentParams = new URLSearchParams(
      location.hash.startsWith('#') ? location.hash.slice(1) : location.hash,
    );
    const queryParams = new URLSearchParams(location.search);

    // Error short-circuit. Errors can come back in either query or
    // fragment depending on which Nexus path emitted them, so check both.
    const errorMsg =
      queryParams.get('oauth_error') ?? fragmentParams.get('oauth_error');
    const resultStatus =
      queryParams.get('oauth_result') ?? fragmentParams.get('oauth_result');
    if (errorMsg || resultStatus === 'error') {
      console.error('OAuth callback returned error:', errorMsg);
      navigate('/login', { state: { oauthError: errorMsg || 'Unknown OAuth error' } });
      return;
    }

    const accessToken = fragmentParams.get('access_token');
    const idToken = fragmentParams.get('id_token');
    const refreshToken = fragmentParams.get('refresh_token');
    const expiresIn = fragmentParams.get('expires_in');

    if (accessToken && refreshToken && expiresIn) {
      // Nexus emits `expires_in` as a relative duration in seconds;
      // convert to the absolute ms timestamp the rest of the app uses.
      const expiresAt = Date.now() + parseInt(expiresIn, 10) * 1000;
      const tokens = {
        accessToken,
        // idToken is optional — Nexus always emits one for OAuth flows,
        // but the type allows undefined for password-login compatibility.
        ...(idToken ? { idToken } : {}),
        refreshToken,
        expiresAt,
      };

      localStorage.setItem('bolusnotes_auth_tokens', JSON.stringify(tokens));

      // Force a navigation that re-runs KStackAuthContext's bootstrap
      // useEffect, which will pick up the new tokens from localStorage.
      window.location.href = '/dashboard';
    } else {
      console.error('OAuth callback missing required tokens', {
        hasAccess: Boolean(accessToken),
        hasRefresh: Boolean(refreshToken),
        hasExpiresIn: Boolean(expiresIn),
      });
      navigate('/login', { state: { oauthError: 'OAuth callback missing tokens' } });
    }
  }, [location, navigate]);

  return (
    <div className="oauth-callback-page">
      <ThemeToggle placement="corner" />
      <div className="oauth-callback-inner">
        <h2>Authenticating…</h2>
        <div className="oauth-spinner" aria-hidden />
      </div>
    </div>
  );
};

export default OAuthCallback;
